import { Link } from "react-router-dom";
import { COMPANY } from "../lib/company";
import { GOOGLE_SCOPES, META_SCOPES } from "../lib/scopes";

const STEPS = [
  {
    n: "01",
    title: "Connect your accounts",
    body: "Authorise on Facebook's and Google's own sites. We receive a revocable token, never your password.",
    tag: "Secure · Revocable · You're in control",
  },
  {
    n: "02",
    title: "Compose once",
    body: "Write the caption, attach the photo or video, and tick the Pages, Instagram accounts and channels it should go to.",
    tag: "One post. Many destinations.",
  },
  {
    n: "03",
    title: "Publish or schedule",
    body: "Send it now, or pick a time. Each destination is delivered independently and reported separately.",
    tag: "Track results. Every channel.",
  },
];

const FEATURES = [
  {
    icon: "↗",
    title: "Cross-platform social media publishing",
    body: "Photos to Facebook Pages and Instagram Business accounts. Video to Facebook, Instagram and YouTube. No re-uploading the same file three times.",
  },
  {
    icon: "◇",
    title: "Limits checked before you post",
    body: "We read the file — size, dimensions, aspect ratio, duration, format — and check it against each network's published limits first. You get a plain sentence, not a rejection an hour later.",
  },
  {
    icon: "✓",
    title: "Honest per-destination status",
    body: "A post is not one status. It is one row per network, each with its own state, its own error and its own link to the live post.",
  },
  {
    icon: "↻",
    title: "Retries that make sense",
    body: "Temporary failures back off and retry on a schedule. Permanent failures stop immediately and tell you exactly what the network said.",
  },
  {
    icon: "◷",
    title: "Schedule social media posts ahead of time",
    body: "Schedules run on a server-side clock that ticks every minute. Your device does not need to be on.",
  },
  {
    icon: "▣",
    title: "Web and mobile",
    body: "Compose from the browser at your desk or prepare content on mobile. See our mobile app page for availability.",
  },
];

const FAQ = [
  {
    q: "Do you ever see my Facebook or Google password?",
    a: "No. You type it on facebook.com or accounts.google.com, never on our site. We receive an access token instead — a revocable key limited to the permissions you approved. Check the address bar when the password field appears; that is the proof.",
  },
  {
    q: "What exactly can you do with my accounts?",
    a: "Publish the posts you compose, and read the name and picture of the Pages, Instagram accounts and channels you connect so we can show them to you. We did not request access to messages, followers, friends, ad accounts or your existing content, so we cannot read them.",
  },
  {
    q: "How do I disconnect?",
    a: "Disconnect inside the app and the stored token is deleted immediately, or remove us from Facebook Business Integrations or your Google security settings without visiting our site at all.",
  },
  {
    q: "Which accounts are supported?",
    a: "Facebook Pages, Instagram Business and Creator accounts linked to a Page, and YouTube channels. Personal Facebook profiles and personal Instagram accounts cannot be published to by any tool — the platforms do not offer an API for it.",
  },
  {
    q: "Is a scheduled post guaranteed to go out?",
    a: "No tool can promise that. Publication depends on the network's API, your token still being valid, and the network's own limits and moderation. What we can promise is that you will always be told precisely what happened and why.",
  },
  {
    q: "What does it cost?",
    // Deliberately no figures here. Prices are regional and come from the
    // catalogue at runtime; a number typed into this answer would be the one
    // place on the site that could contradict the pricing page.
    a: (
      <>
        There is a free plan &mdash; one connected account and ten posts a month, without
        scheduling or video. Paid plans add scheduling, video publishing and more accounts,
        monthly or yearly, and there is a one-off Lifetime plan. Every price is shown in your
        own currency on the{" "}
        <Link to="/pricing">pricing page</Link>, before you are charged, and nothing upgrades
        automatically.
      </>
    ),
  },
];

export function Landing() {
  return (
    <>
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-copy">
            <p className="hero-badge"><span aria-hidden="true">✦</span> AI-Assisted. Human-Led.</p>
            <p className="hero-product-name">{COMPANY.product}</p>
            <h1>
              <span className="hero-gradient-text">AI-Assisted Social Media Management,</span>{" "}
              <span>With You in Control</span>
            </h1>
            <p className="lede">
              Plan, create, schedule and publish your social content from one workspace.
              Use AI to help prepare your content while you stay in control of your brand,
              voice and publishing decisions.
            </p>
            <div className="hero-cta">
              <Link to="/signin" className="btn-link primary lg">Get started free <span aria-hidden="true">→</span></Link>
              <Link to="/how-it-works" className="btn-link lg">See how it works <span aria-hidden="true">◉</span></Link>
            </div>
            <div className="hero-benefits" aria-label="Product benefits">
              <span><i className="benefit-icon blue" aria-hidden="true">↗</i> Multi-platform<br />publishing</span>
              <span><i className="benefit-icon green" aria-hidden="true">◷</i> Reliable<br />scheduling</span>
              <span><i className="benefit-icon violet" aria-hidden="true">✓</i> Pre-publish<br />checks</span>
            </div>
          </div>

          <div className="hero-visual" aria-label="Simino Social scheduling preview">
            <section className="landing-scheduling-card" aria-labelledby="landing-scheduling-title">
              <div className="landing-scheduling-art" aria-hidden="true">
                <svg viewBox="0 0 80 88" fill="none">
                  <rect x="9" y="15" width="62" height="64" rx="12" fill="currentColor" opacity=".2" />
                  <rect x="13" y="11" width="58" height="64" rx="10" fill="var(--panel)" stroke="currentColor" strokeWidth="3" />
                  <path d="M14 22a10 10 0 0 1 10-10h36a10 10 0 0 1 10 10v10H14Z" fill="currentColor" />
                  <path d="M28 6v15M56 6v15" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
                  {[0, 1].map(row => [0, 1, 2].map(col => <rect key={`${row}-${col}`} x={24 + col * 13} y={41 + row * 14} width="8" height="8" rx="2" fill="currentColor" opacity={row === 0 && col === 1 ? 1 : .45} />))}
                </svg>
              </div>
              <div className="landing-scheduling-copy">
                <h2 id="landing-scheduling-title">Why scheduling matters</h2>
                <p>For solo, nano &amp; micro influencers</p>
                <ul className="landing-scheduling-benefits">
                  <li><span className="benefit-icon violet" aria-hidden="true">↗</span><span><strong>Stay consistent</strong><small>Keep showing up</small></span></li>
                  <li><span className="benefit-icon blue" aria-hidden="true">◷</span><span><strong>Choose your timing</strong><small>Plan for your audience</small></span></li>
                  <li><span className="benefit-icon violet" aria-hidden="true">ϟ</span><span><strong>Save time, less stress</strong><small>Focus on what you love</small></span></li>
                </ul>
                <Link to="/resources/why-scheduling" className="landing-scheduling-link" aria-label="Learn more about why scheduling matters">Learn more <span aria-hidden="true">→</span></Link>
              </div>
            </section>
            <div className="calendar-shell">
              <div className="calendar-rail" aria-hidden="true">
                <img src="/mark.png" alt="" />
                <span>⌂</span><span className="active">▣</span><span>□</span><span>↗</span>
              </div>
              <div className="calendar-main">
                <div className="calendar-head">
                  <div><span className="mock-kicker">Publishing calendar</span><strong>This week</strong></div>
                  <span className="calendar-chip">All channels⌄</span>
                </div>
                <div className="calendar-days" aria-hidden="true">
                  {[
                    ["MON", "12"], ["TUE", "13"], ["WED", "14"],
                    ["THU", "15"], ["FRI", "16"],
                  ].map(([day, date]) => <span key={day}><small>{day}</small>{date}</span>)}
                </div>
                <div className="calendar-grid" aria-hidden="true">
                  <span></span><span className="mock-post insta">IG <b>Product story</b><small>10:00 AM</small></span>
                  <span></span><span className="mock-post youtube">▶ <b>Weekly video</b><small>12:30 PM</small></span>
                  <span></span><span className="mock-post facebook">f <b>Page update</b><small>2:15 PM</small></span>
                  <span></span><span></span><span></span><span></span>
                </div>
              </div>
            </div>
            <div className="status-float">
              <span className="status-label">Ready to publish</span>
              <strong>3 destinations</strong>
              <div><span className="network-dot fb">f</span><span className="network-dot ig">◎</span><span className="network-dot yt">▶</span></div>
              <small><i></i> File checks passed</small>
            </div>
            <div className="delivery-float">
              <span>Delivery status</span><strong>All clear</strong>
              <div className="mini-line" aria-hidden="true"><i></i></div>
            </div>
          </div>
        </div>
      </section>

      <section className="strip">
        <div className="strip-inner">
          <div className="platforms-title"><i aria-hidden="true">↗</i><span>Publishes to</span></div>
          <div className="platform-pills">
            <span className="platform-pill facebook"><i aria-hidden="true">f</i>Facebook Pages</span>
            <span className="platform-pill instagram"><i aria-hidden="true">◎</i>Instagram Business</span>
            <span className="platform-pill youtube"><i aria-hidden="true">▶</i>YouTube</span>
          </div>
        </div>
      </section>

      <section className="band works-band">
        <div className="band-inner">
          <div className="works-head">
            <p className="hero-badge"><span aria-hidden="true">✦</span> Simple. Secure. Powerful.</p>
            <h2 className="section-title">How it <span>works</span></h2>
            <p>Start publishing across your channels in just a few steps.</p>
          </div>
          <div className="steps">
            {STEPS.map((s) => (
              <div className="step" key={s.n}>
                <span className="step-n">{s.n}</span>
                <div className={`step-art step-art-${s.n}`} aria-hidden="true">
                  {s.n === "01" && <><i className="art-fb">f</i><b></b><i className="art-google">G</i><span>♢</span></>}
                  {s.n === "02" && <><div><b>Aa</b><span></span><span></span><em>＋</em></div><i className="art-fb">f</i><i className="art-ig">◎</i></>}
                  {s.n === "03" && <><div className="art-calendar"><b>16</b><span>◷</span></div><div className="art-status"><span><i className="art-fb">f</i> Sent</span><span><i className="art-ig">◎</i> Scheduled</span><span><i className="art-yt">▶</i> Scheduled</span></div></>}
                </div>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
                <span className="step-tag"><i aria-hidden="true">✓</i>{s.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band alt features-band">
        <div className="band-inner">
          <h2 className="section-title">Social media management for your working week</h2>
          <p className="lede">Plan posts in your social media content calendar and manage connected accounts from one place. Use brand-aware prompts in your own AI tool, then import and review your captions before publishing. Explore our <Link to="/social-media-scheduler">social media scheduler</Link> for a calmer weekly routine.</p>
          <div className="grid">
            {FEATURES.map((f) => (
              <div className="feature" key={f.title}>
                <span className="feature-icon" aria-hidden="true">{f.icon}</span>
                <h3>{f.title}</h3>
                <p>{f.body}{f.title === "Web and mobile" && <> <Link to="/app">Mobile app availability</Link></>}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band">
        <div className="band-inner narrow">
          <h2 className="section-title">Built to be checked, not just trusted</h2>
          <p className="lede">
            A new tool asking for access to your business accounts should expect scepticism. So the
            answers are written down rather than implied: which permissions we request and the single
            step each one is needed for, how tokens are encrypted and where the key is kept, why your
            media sits in a private bucket behind links that expire in two hours, and how to revoke
            everything in three clicks without asking us.
          </p>

          {/* The permission table sits on the home page, in the open, rather than
              behind a link. Google requires the home page itself to explain the
              purpose for which the app requests user data, and a reviewer should
              not have to click or expand anything to find it. */}
          <h3 className="permissions-title">What we ask for, and why</h3>
          <p className="permissions-note">
            Every permission below is requested for one named step. Nothing is requested just in
            case. You approve them on Facebook&rsquo;s and Google&rsquo;s own consent screens, and
            you can withdraw them at any time without visiting this site.
          </p>
          <div className="permissions">
            <div className="permission-group meta">
              <h4>
                <i className="perm-icon fb" aria-hidden="true">f</i>
                <i className="perm-icon ig" aria-hidden="true">&#9678;</i>
                Facebook Pages and Instagram
              </h4>
              <dl>
                {META_SCOPES.map(([scope, why]) => (
                  <div key={scope}><dt><code>{scope}</code></dt><dd>{why}</dd></div>
                ))}
              </dl>
            </div>
            <div className="permission-group youtube">
              <h4>
                <i className="perm-icon yt" aria-hidden="true">&#9654;</i>
                YouTube
              </h4>
              <dl>
                {GOOGLE_SCOPES.map(([scope, why]) => (
                  <div key={scope}><dt><code>{scope}</code></dt><dd>{why}</dd></div>
                ))}
              </dl>
            </div>
          </div>
          <p className="permissions-note">
            We did not request access to your messages, followers, friends, ad accounts or your
            existing content, so we cannot read them. {COMPANY.product}&rsquo;s use of information
            received from Google APIs adheres to the Google API Services User Data Policy, including
            the Limited Use requirements &mdash; set out in full in our{" "}
            <Link to="/privacy">Privacy Policy</Link>.
          </p>
          <p>
            <Link to="/security" className="btn-link primary">Read the security page</Link>
          </p>
        </div>
      </section>

      <section className="band alt">
        <div className="band-inner narrow">
          <h2 className="section-title">Questions</h2>
          <div className="faq">
            {FAQ.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="cta">
        <div className="cta-inner">
          <span className="cta-social cta-instagram" aria-hidden="true">◎</span>
          <span className="cta-social cta-facebook" aria-hidden="true">f</span>
          <span className="cta-social cta-youtube" aria-hidden="true">▶</span>
          <h2>Ready to simplify your social publishing?</h2>
          <p>
            Compose once, schedule confidently, and follow every destination from one clear workspace.
          </p>
          <Link to="/signin" className="btn-link primary lg">Get started free <span aria-hidden="true">→</span></Link>
          <div className="cta-assurances" aria-label="Getting started details">
            <span><i aria-hidden="true">✓</i>No credit card required</span>
            <span><i aria-hidden="true">✓</i>No web-app download</span>
            <span><i aria-hidden="true">✓</i>Disconnect anytime</span>
          </div>
        </div>
      </section>
    </>
  );
}
